if (!customElements.get("quick-add-modal")) {
  const styleLoads = new Map();
  const loadQuickViewStyles = () => {
    const template = document.getElementById("QuickViewStyles");
    if (!template) return Promise.resolve();

    return Promise.all(
      Array.from(template.content.querySelectorAll("link")).map((source) => {
        const href = new URL(source.getAttribute("href"), document.baseURI).href;
        if (styleLoads.has(href)) return styleLoads.get(href);

        const existing = Array.from(
          document.querySelectorAll('link[rel="stylesheet"]')
        ).find((link) => link.href === href);
        if (existing?.sheet) {
          if (existing.media === "print") existing.media = "all";
          return Promise.resolve();
        }

        const link = existing || source.cloneNode();
        const promise = new Promise((resolve, reject) => {
          const cleanup = () => {
            clearTimeout(timeout);
            link.removeEventListener("load", loaded);
            link.removeEventListener("error", failed);
          };
          const loaded = () => {
            cleanup();
            resolve();
          };
          const failed = (event) => {
            cleanup();
            if (!existing || event?.type === "error") link.remove();
            reject(new Error("Quick view styles could not be loaded"));
          };
          const timeout = setTimeout(failed, 10000);
          link.addEventListener("load", loaded);
          link.addEventListener("error", failed);
          if (!existing) template.before(link);
        }).catch((error) => {
          styleLoads.delete(href);
          throw error;
        });
        styleLoads.set(href, promise);
        return promise;
      })
    );
  };

  const prepareQuickView = (event) => {
    if (event.target?.closest?.("modal-opener button[data-product-url]")) {
      loadQuickViewStyles().catch(() => {});
    }
  };
  document.addEventListener("pointerover", prepareQuickView, { passive: true });
  document.addEventListener("pointerdown", prepareQuickView, { passive: true });
  document.addEventListener("focusin", prepareQuickView);

  customElements.define(
    "quick-add-modal",
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.quickViewMainSwiper = null;
        this.quickViewThumbsSwiper = null;
      }

      hide(preventFocus = false) {
        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer) cartDrawer.setActiveElement(this.openedBy);
        this.destroySlider();
        this.modalContent.innerHTML = "";

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        if (this.loading) return;
        this.loading = true;
        opener.setAttribute("aria-disabled", true);
        opener.setAttribute("aria-busy", true);
        opener.classList.add("loading");

        if (opener.querySelector(".loading-overlay__spinner")) {
          opener
            .querySelector(".loading-overlay__spinner")
            .classList.remove("hidden");
        }

        Promise.all([
          loadQuickViewStyles(),
          fetch(opener.getAttribute("data-product-url")).then((response) => {
            if (!response.ok) throw new Error("Quick view product could not be loaded");
            return response.text();
          }),
        ])
          .then(([, responseText]) => {
            const responseHTML = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.productElement = responseHTML.querySelector(
              '[id^="MainProduct-"]'
            );
            if (!this.productElement) throw new Error("Quick view product is missing");
            this.preventDuplicatedIDs();
            this.removeDOMElements();
            this.setInnerHTML(
              this.modalContent,
              this.productElement.innerHTML,
              opener
            );

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.removeGalleryListSemantic();
            this.updateImageSizes();
            this.preventVariantURLSwitching();
            super.show(opener);
            this.initSlider();
          })
          .then(() => {
            function fallbackCopyTextToClipboard(text) {
              const textarea = document.createElement("textarea");
              textarea.value = text;
              textarea.style.position = "fixed";
              document.body.appendChild(textarea);
              textarea.focus();
              textarea.select();
              try {
                const successful = document.execCommand("copy");
                return successful;
              } catch (err) {
                return false;
              } finally {
                document.body.removeChild(textarea);
              }
            }

            function copyLink(e, copyButton) {
              e.preventDefault();
              e.stopPropagation();
              const url = copyButton.getAttribute("href");

              const handleCopy = (btn, success) => {
                if (success) {
                  btn.classList.add("copied");
                  setTimeout(() => {
                    btn.classList.remove("copied");
                  }, 1000);
                } else {
                  btn.classList.add("notCopied");
                  setTimeout(() => {
                    btn.classList.remove("notCopied");
                  }, 1000);
                }
              };

              if (window.Shopify && window.Shopify.designMode) {
                const success = fallbackCopyTextToClipboard(url);
                handleCopy(copyButton, success);
              } else {
                navigator.clipboard.writeText(url).then(
                  () => {
                    handleCopy(copyButton, true);
                  },
                  () => {
                    handleCopy(copyButton, false);
                  }
                );
              }
            }

            const copyButtons = this.modalContent.querySelectorAll(".copy-btn");

            if (copyButtons.length) {
              for (const copyButton of copyButtons) {
                copyButton.addEventListener("click", (e) =>
                  copyLink(e, copyButton)
                );
              }
            }

            if ($("#search-modal").hasClass("active")) {
              $("#search-modal,.search-modal__mask").toggleClass("active");
            }
          })
          .catch(() => {
            if (this.hasAttribute("open")) this.hide();
            const productUrl = opener.getAttribute("data-product-url");
            if (productUrl) window.location.assign(productUrl);
          })
          .finally(() => {
            this.loading = false;
            opener.removeAttribute("aria-disabled");
            opener.removeAttribute("aria-busy");
            opener.classList.remove("loading");
            opener.querySelector(".loading-overlay__spinner")?.classList.add("hidden");
          });
      }

      setInnerHTML(element, html, opener) {
        element.innerHTML = html;

        // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
        element.querySelectorAll("script").forEach((oldScriptTag) => {
          const newScriptTag = document.createElement("script");
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(
            document.createTextNode(oldScriptTag.innerHTML)
          );
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });

        // Read more button
        const moreBtn = document.createElement("a");
        moreBtn.textContent = `${theme.quickviewMore}`;
        moreBtn.setAttribute("href", opener.getAttribute("data-product-url"));
        moreBtn.setAttribute(
          "class",
          "product__read-more button--simple full-unstyled-link h6"
        );
        element.querySelector(".product__info-container").appendChild(moreBtn);
      }

      removeDOMElements() {
        const pickupAvailability = this.productElement.querySelector(
          "pickup-availability"
        );
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = this.productElement.querySelector("product-modal");
        if (productModal) productModal.remove();

        const gift = this.productElement.querySelector(".customer");
        if (gift) gift.remove();
      }

      destroySlider(container = this.modalContent) {
        if (!container) return;

        const mainSliderElement = container.querySelector(".js-media-list");
        const thumbsSliderElement =
          container.querySelector(".js-media-sublist");

        if (mainSliderElement && mainSliderElement.swiper) {
          mainSliderElement.swiper.destroy(true, true);
        }
        if (thumbsSliderElement && thumbsSliderElement.swiper) {
          thumbsSliderElement.swiper.destroy(true, true);
        }

        this.quickViewMainSwiper = null;
        this.quickViewThumbsSwiper = null;
      }

      initSlider(activeMediaId = null) {
        const container = this.modalContent;
        if (!container) return;

        this.destroySlider(container);

        const mainSliderElement = container.querySelector(".js-media-list");
        if (!mainSliderElement) return;

        // Prevent stale inline heights from previous auto-height slider instances.
        mainSliderElement.style.removeProperty("height");
        mainSliderElement
          .querySelector(".swiper-wrapper")
          ?.style.removeProperty("height");

        const thumbsSliderElement =
          container.querySelector(".js-media-sublist");
        const nextButton = container.querySelector(".swiper-btn--next");
        const prevButton = container.querySelector(".swiper-btn--prev");
        const pagination = container.querySelector(".swiper-pagination");

        if (thumbsSliderElement) {
          const thumbsDirection =
            thumbsSliderElement.dataset?.thumbsDirection || "horizontal";
          this.quickViewThumbsSwiper = new Swiper(thumbsSliderElement, {
            slidesPerView: 4,
            spaceBetween: 4,
            watchSlidesProgress: true,
            slideToClickedSlide: true,
            freeMode: true,
            direction: thumbsDirection,
            breakpoints: {
              0: {
                direction: "horizontal",
              },
              1100: {
                direction: thumbsDirection,
              },
            },
          });
        }

        this.quickViewMainSwiper = new Swiper(mainSliderElement, {
          slidesPerView: 1,
          autoHeight: false,
          navigation: {
            nextEl: nextButton,
            prevEl: prevButton,
          },
          pagination: {
            el: pagination,
            clickable: true,
          },
          thumbs: this.quickViewThumbsSwiper
            ? {
                swiper: this.quickViewThumbsSwiper,
              }
            : undefined,
          on: {
            slideChange: () => {
              window.pauseAllMedia();
              if (this.quickViewThumbsSwiper) {
                this.quickViewThumbsSwiper.slideTo(
                  this.quickViewMainSwiper.activeIndex
                );
              }
            },
          },
        });

        if (!activeMediaId || !this.quickViewMainSwiper) return;

        let activeSlideIndex = this.quickViewMainSwiper.slides.findIndex(
          (slideEl) => {
            return slideEl.dataset?.mediaId === activeMediaId;
          }
        );

        if (activeSlideIndex === -1) {
          activeSlideIndex = 0;
        }

        this.quickViewMainSwiper.slideTo(activeSlideIndex, 0);
        if (this.quickViewThumbsSwiper) {
          this.quickViewThumbsSwiper.slideTo(activeSlideIndex, 0);
        }
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        this.productElement.innerHTML =
          this.productElement.innerHTML.replaceAll(
            sectionId,
            `quickadd-${sectionId}`
          );
        this.productElement
          .querySelectorAll("variant-selects, variant-radios")
          .forEach((variantSelect) => {
            variantSelect.dataset.originalSection = sectionId;
          });
        // Section Rendering URLs must keep the real section id; the prefixed id returns 404.
        this.productElement
          .querySelectorAll("product-recommendations--single[data-url]")
          .forEach((recommendations) => {
            recommendations.dataset.url = recommendations.dataset.url.replaceAll(
              `quickadd-${sectionId}`,
              sectionId
            );
          });
      }

      preventVariantURLSwitching() {
        if (this.modalContent.querySelector("variant-radios,variant-selects")) {
          this.modalContent
            .querySelector("variant-radios,variant-selects")
            .setAttribute("data-update-url", "false");
        }
      }

      removeGalleryListSemantic() {
        const galleryList = this.modalContent.querySelector(
          '[id^="Slider-Gallery"]'
        );
        if (!galleryList) return;

        galleryList.setAttribute("role", "presentation");
        galleryList
          .querySelectorAll('[id^="Slide-"]')
          .forEach((li) => li.setAttribute("role", "presentation"));
      }

      updateImageSizes() {
        const product = this.modalContent.querySelector(".product");
        const desktopColumns = product.classList.contains("product--columns");
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll(".product__media img");
        if (!mediaImages.length) return;

        let mediaImageSizes =
          "(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)";

        if (product.classList.contains("product--medium")) {
          mediaImageSizes = mediaImageSizes.replace("715px", "605px");
        } else if (product.classList.contains("product--small")) {
          mediaImageSizes = mediaImageSizes.replace("715px", "495px");
        }

        mediaImages.forEach((img) =>
          img.setAttribute("sizes", mediaImageSizes)
        );
      }
    }
  );
}
